/**
 * Vercel Analytics Service
 * 
 * Provides custom event tracking for Vercel Web Analytics.
 * Tracks user interactions, feature usage, and performance metrics.
 */

import { track } from '@vercel/analytics';

export class VercelAnalytics {
    /**
     * Track scenario switch between Bake-Off (A) and Content Steering (B)
     * @param scenario - The scenario being switched to ('a' or 'b')
     */
    static trackScenarioSwitch(scenario: 'a' | 'b'): void {
        track('scenario_switch', {
            scenario,
            scenarioName: scenario === 'a' ? 'Bake-Off' : 'Content Steering'
        });
    }

    /**
     * Track player errors with video and CDN context
     * @param error - Error message or code
     * @param player - Player type (shaka, videojs, theo)
     * @param videoName - Name of the video/stream
     * @param videoUrl - URL of the video/stream
     * @param cdnProvider - CDN serving the content (optional)
     */
    static trackPlayerError(
        error: string,
        player: string,
        videoName: string,
        videoUrl: string,
        cdnProvider?: string
    ): void {
        track('player_error', {
            error,
            player,
            videoName,
            videoUrl,
            ...(cdnProvider && { cdnProvider })
        });
    }

    /**
     * Track time to max quality with comprehensive performance data
     * @param ttmq - Time to max quality in milliseconds
     * @param videoName - Name of the video/stream
     * @param videoUrl - URL of the video/stream
     * @param cdnProvider - CDN serving the content (optional)
     * @param additionalMetrics - Optional additional metrics (all time values in milliseconds)
     */
    static trackTimeToMaxQuality(
        ttmq: number,
        videoName: string,
        videoUrl: string,
        cdnProvider?: string,
        additionalMetrics?: {
            ttff?: number;  // Time to first frame in ms
            ttfb?: number;  // Time to first byte in ms
            downloadSpeed?: number;  // bits per second
        }
    ): void {
        track('time_to_max_quality', {
            ttmq,
            videoName,
            videoUrl,
            ...(cdnProvider && { cdnProvider }),
            ...(additionalMetrics?.ttff && { ttff: additionalMetrics.ttff }),
            ...(additionalMetrics?.ttfb && { ttfb: additionalMetrics.ttfb }),
            ...(additionalMetrics?.downloadSpeed && { downloadSpeed: additionalMetrics.downloadSpeed })
        });
    }

    /**
     * Track delivery path visualization on geomap
     * Captures which stream was played, which CDN served it, and the complete delivery route
     * @param data - Delivery path information
     */
    static trackDeliveryPath(data: {
        streamName: string;
        streamUrl: string;
        cdnProvider: string;
        cdnLocation: string;
        pathLength: number;
        route: Array<{ type: string; label: string; }>;
        userLocation: string;
    }): void {
        // Serialize route to string (Vercel Analytics doesn't accept arrays)
        const routeString = data.route.map(node => `${node.type}:${node.label}`).join(' → ');

        track('delivery_path', {
            streamName: data.streamName,
            streamUrl: data.streamUrl,
            cdnProvider: data.cdnProvider,
            cdnLocation: data.cdnLocation,
            pathLength: data.pathLength,
            route: routeString,
            userLocation: data.userLocation
        });
    }

}
